import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const MIN_FILTER_WIDTH = 255;

// Reuse a single canvas element to avoid memory leaks
let measurementCanvas: HTMLCanvasElement | null = null;

/**
 * Measures text width using canvas with actual computed font styles
 * Reuses a single canvas element to prevent memory leaks
 */
const measureTextWidth = (
  text: string,
  element?: HTMLElement | null,
): number => {
  if (!measurementCanvas) {
    measurementCanvas = document.createElement("canvas");
  }
  const context = measurementCanvas.getContext("2d");
  if (!context) return 0;

  if (element) {
    // Use computed styles from the actual element for accuracy
    const computedStyle = window.getComputedStyle(element);
    const fontWeight = computedStyle.fontWeight;
    const fontSize = computedStyle.fontSize;
    const fontFamily = computedStyle.fontFamily;
    context.font = `${fontWeight} ${fontSize} ${fontFamily}`;
  } else {
    // Fallback to default
    context.font = "normal 10px system-ui, -apple-system, sans-serif";
  }

  return context.measureText(text).width;
};

/**
 * Filter item selector configuration
 */
interface FilterItemSelectors {
  /** Selector for filter item containers (e.g., '[role="menuitem"]' or '.ranked-list-item') */
  itemSelector: string;
  /** Selector for label elements (e.g., 'span.text-gray-600' or 'span.text-gray-900') */
  labelSelector: string;
  /** Selector for count/percentage elements (e.g., 'output' or '.count-container') */
  countSelector?: string;
  /** Selector for checkbox elements (optional) */
  checkboxSelector?: string;
  /** Additional elements to account for (e.g., rank, color dot) - returns width to add */
  getAdditionalWidth?: (item: HTMLElement) => number;
}

/**
 * Default selectors for News Feed filters
 */
const DEFAULT_NEWS_FEED_SELECTORS: FilterItemSelectors = {
  itemSelector: '[role="menuitem"]',
  labelSelector: "span.text-gray-600, button span.text-gray-600",
  countSelector: "output",
  checkboxSelector: 'input[type="checkbox"], [role="checkbox"]',
};

/**
 * Default selectors for Share of Voice filters (RankedListItem)
 */
const DEFAULT_SOV_SELECTORS: FilterItemSelectors = {
  itemSelector: ".flex.items-center.justify-between", // RankedListItem container
  labelSelector: "span.text-gray-900",
  countSelector: ".text-gray-600", // Count/percentage container
  getAdditionalWidth: (item) => {
    // Account for rank number (#1, #2, etc.) and color dot
    const rankElement = item.querySelector<HTMLElement>(
      '[class*="text-[9px]"]',
    );
    const colorDot = item.querySelector<HTMLElement>(
      '[class*="rounded-full"][class*="w-2"]',
    );
    let additionalWidth = 0;
    if (rankElement) {
      additionalWidth += rankElement.offsetWidth || 28; // w-7 = 28px
    }
    if (colorDot) {
      additionalWidth += 8; // w-2 = 8px
    }
    // Account for spacing (space-x-2 = 8px between elements)
    if (rankElement || colorDot) {
      additionalWidth += 8; // space-x-2
    }
    return additionalWidth;
  },
};

/** Result of measuring filter container for max width. */
interface MaxWidthMeasurement {
  maxItemWidth: number;
  itemCount: number;
}

/**
 * Calculates the maximum width needed for filter items based on longest label
 * and returns the number of matching items (so callers can treat 0–1 items as empty/placeholder).
 */
const calculateMaxItemWidth = (
  container: HTMLElement | null,
  selectors: FilterItemSelectors,
): MaxWidthMeasurement => {
  if (!container) return { maxItemWidth: 0, itemCount: 0 };

  const filterItems = container.querySelectorAll<HTMLElement>(
    selectors.itemSelector,
  );
  const itemCount = filterItems.length;
  let maxItemWidth = 0;

  for (const item of filterItems) {
    const labelElement = item.querySelector<HTMLElement>(
      selectors.labelSelector,
    );
    const countElement = selectors.countSelector
      ? item.querySelector<HTMLElement>(selectors.countSelector)
      : null;
    const checkboxElement = selectors.checkboxSelector
      ? item.querySelector<HTMLElement>(selectors.checkboxSelector)
      : null;

    let itemWidth = 0;

    // Get computed styles for padding
    const itemStyles = window.getComputedStyle(item);
    const paddingLeft = Number.parseFloat(itemStyles.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(itemStyles.paddingRight) || 0;

    // Add container padding
    itemWidth += paddingLeft + paddingRight;

    // Add checkbox width and margin (if present)
    if (checkboxElement) {
      const checkboxStyles = window.getComputedStyle(checkboxElement);
      itemWidth += checkboxElement.offsetWidth || 20;
      itemWidth += Number.parseFloat(checkboxStyles.marginRight) || 4;
    } else if (selectors.checkboxSelector) {
      // Only add default if checkbox was expected
      itemWidth += 20; // Default checkbox width
      itemWidth += 4; // Default margin
    }

    // Add additional width (e.g., rank, color dot for SOV)
    if (selectors.getAdditionalWidth) {
      itemWidth += selectors.getAdditionalWidth(item);
    }

    // Measure label text with actual font
    if (labelElement) {
      const labelText = labelElement.textContent?.trim();
      if (labelText) {
        itemWidth += measureTextWidth(labelText, labelElement);
      }
    }

    // Measure count text and spacing
    if (countElement) {
      const countStyles = window.getComputedStyle(countElement);
      const countText = countElement.textContent?.trim();
      if (countText) {
        // Try to get margin-left, fallback to default spacing
        const marginLeft = Number.parseFloat(countStyles.marginLeft) || 0;
        if (marginLeft > 0) {
          itemWidth += marginLeft;
        } else {
          // Default spacing if no margin
          itemWidth += 10;
        }
        itemWidth += measureTextWidth(countText, countElement);
      }
    }

    if (itemWidth > 0) {
      maxItemWidth = Math.max(maxItemWidth, itemWidth);
    }
  }

  return { maxItemWidth, itemCount };
};

/**
 * Options for configuring the useFilterMaxWidth hook
 */
interface UseFilterMaxWidthOptions {
  /** Whether the sidebar is expanded */
  isExpanded: boolean;
  /** Whether resizing is currently active - prevents recalculation during resize */
  isResizing?: boolean;
  /** Maximum percentage of viewport width (default: 0.9) */
  maxViewportPercentage?: number;
  /** Debounce delay in ms for mutation observer (default: 300) */
  debounceDelay?: number;
  /** Filter item selectors - defaults to News Feed structure */
  selectors?: Partial<FilterItemSelectors>;
  /** Filter type preset - 'news-feed' or 'share-of-voice' (default: 'news-feed') */
  filterType?: "news-feed" | "share-of-voice";
}

/**
 * Return value from useFilterMaxWidth hook
 */
interface UseFilterMaxWidthReturn {
  /** The calculated max width in pixels */
  maxWidth: number;
  /** Ref to attach to the filter container element */
  filterContainerRef: React.RefObject<HTMLDivElement | null>;
  /** Function to manually recalculate max width */
  recalculateMaxWidth: () => void;
}

/**
 * Hook to calculate and maintain max width based on longest filter label.
 *
 * This hook dynamically calculates the maximum width needed for a filter sidebar
 * by measuring the actual rendered width of filter items, including labels, counts,
 * checkboxes, and additional elements. The calculated width is constrained between
 * MIN_FILTER_WIDTH (255px) and a percentage of the viewport width.
 *
 * The hook automatically:
 * - Recalculates when filter content changes (via MutationObserver)
 * - Updates on window resize
 * - Prevents recalculation during active resizing to avoid interference
 * - Debounces updates to prevent performance issues
 *
 * @param options - Configuration options for the hook
 * @param options.isExpanded - Whether the sidebar is expanded (required)
 * @param options.isResizing - Whether resizing is currently active (optional, default: false)
 * @param options.maxViewportPercentage - Maximum percentage of viewport width (optional, default: 0.9)
 * @param options.debounceDelay - Debounce delay in ms for mutation observer (optional, default: 300)
 * @param options.selectors - Custom filter item selectors (optional)
 * @param options.filterType - Filter type preset: 'news-feed' or 'share-of-voice' (optional, default: 'news-feed')
 * @returns An object containing maxWidth, filterContainerRef, and recalculateMaxWidth
 *
 * @example
 * Basic usage for News Feed filters:
 * ```ts
 * const { maxWidth, filterContainerRef } = useFilterMaxWidth({
 *   isExpanded: true,
 *   isResizing: false,
 *   maxViewportPercentage: 0.9,
 *   filterType: 'news-feed',
 * });
 * ```
 *
 * @example
 * Usage for Share of Voice filters:
 * ```ts
 * const { maxWidth, filterContainerRef } = useFilterMaxWidth({
 *   isExpanded: true,
 *   filterType: 'share-of-voice',
 *   maxViewportPercentage: 0.33,
 * });
 * ```
 */
export const useFilterMaxWidth = (
  options: UseFilterMaxWidthOptions,
): UseFilterMaxWidthReturn => {
  const {
    isExpanded,
    isResizing = false,
    maxViewportPercentage = 0.9,
    debounceDelay = 300,
    selectors: customSelectors,
    filterType = "news-feed",
  } = options;

  // Determine which selectors to use
  const baseSelectors =
    filterType === "share-of-voice"
      ? DEFAULT_SOV_SELECTORS
      : DEFAULT_NEWS_FEED_SELECTORS;

  // Memoize selectors so object identity is stable for useEffect dependencies.
  const selectors = useMemo<FilterItemSelectors>(
    () => ({
      ...baseSelectors,
      ...customSelectors,
      // Merge getAdditionalWidth functions if both exist
      getAdditionalWidth:
        customSelectors?.getAdditionalWidth || baseSelectors.getAdditionalWidth,
    }),
    [baseSelectors, customSelectors],
  );

  const [maxWidth, setMaxWidth] = useState(
    window.innerWidth * maxViewportPercentage,
  );
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(isResizing);
  const isMountedRef = useRef(true);
  const resizeEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear any pending resize end timer
      if (resizeEndTimerRef.current) {
        clearTimeout(resizeEndTimerRef.current);
        resizeEndTimerRef.current = null;
      }
    };
  }, []);

  // Keep resize ref in sync and handle resize end delay
  useEffect(() => {
    isResizingRef.current = isResizing;

    // Clear any existing timer
    if (resizeEndTimerRef.current) {
      clearTimeout(resizeEndTimerRef.current);
      resizeEndTimerRef.current = null;
    }

    // When resizing ends, wait a bit before allowing recalculation
    // This prevents race conditions where filters change during resize
    if (!isResizing && isExpanded && filterContainerRef.current) {
      resizeEndTimerRef.current = setTimeout(() => {
        if (isMountedRef.current && !isResizingRef.current) {
          updateMaxWidthFromLabels();
        }
      }, 150);
    }

    return () => {
      if (resizeEndTimerRef.current) {
        clearTimeout(resizeEndTimerRef.current);
        resizeEndTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizing, isExpanded]); // updateMaxWidthFromLabels is stable due to useMemo on selectors

  /**
   * Calculates and updates max width based on longest filter label
   * Doesn't update if currently resizing or component is unmounted
   */
  const updateMaxWidthFromLabels = useCallback(() => {
    if (
      !isMountedRef.current ||
      !filterContainerRef.current ||
      !isExpanded ||
      isResizingRef.current
    ) {
      return;
    }

    const { maxItemWidth, itemCount } = calculateMaxItemWidth(
      filterContainerRef.current,
      selectors,
    );

    if (!isMountedRef.current) return;

    const viewportMax = window.innerWidth * maxViewportPercentage;
    const useFallback = itemCount <= 1 || maxItemWidth <= 0;
    const calculatedMaxWidth = useFallback
      ? Math.max(MIN_FILTER_WIDTH, viewportMax)
      : Math.max(MIN_FILTER_WIDTH, Math.min(maxItemWidth, viewportMax));

    setMaxWidth(calculatedMaxWidth);
  }, [isExpanded, maxViewportPercentage, selectors]);

  // Expose manual recalculation function
  const recalculateMaxWidth = useCallback(() => {
    updateMaxWidthFromLabels();
  }, [updateMaxWidthFromLabels]);

  // Update max width when filters change (debounced to avoid interfering with resize)
  useEffect(() => {
    if (!isExpanded) return;

    let debounceTimer: ReturnType<typeof setTimeout>;
    let observer: MutationObserver | null = null;

    // Initial calculation
    const timeoutId = setTimeout(() => {
      updateMaxWidthFromLabels();
    }, 100);

    // Watch for filter changes with debouncing
    if (filterContainerRef.current) {
      observer = new MutationObserver(() => {
        // Debounce updates to avoid interfering with resize
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          // Check if still mounted before updating
          if (isMountedRef.current && !isResizingRef.current) {
            updateMaxWidthFromLabels();
          }
        }, debounceDelay);
      });

      observer.observe(filterContainerRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(debounceTimer);
      observer?.disconnect();
    };
  }, [isExpanded, updateMaxWidthFromLabels, debounceDelay]);

  // Update max width on window resize
  useEffect(() => {
    if (!isExpanded) return;

    const handleResize = () => {
      if (
        isMountedRef.current &&
        filterContainerRef.current &&
        !isResizingRef.current
      ) {
        setTimeout(() => {
          // Double-check mounted state after timeout
          if (
            !isMountedRef.current ||
            !filterContainerRef.current ||
            isResizingRef.current
          ) {
            return;
          }
          const { maxItemWidth, itemCount } = calculateMaxItemWidth(
            filterContainerRef.current,
            selectors,
          );
          if (!isMountedRef.current) return;
          const viewportMax = window.innerWidth * maxViewportPercentage;
          const useFallback = itemCount <= 1 || maxItemWidth <= 0;
          const newMaxWidth = useFallback
            ? Math.max(MIN_FILTER_WIDTH, viewportMax)
            : Math.max(MIN_FILTER_WIDTH, Math.min(maxItemWidth, viewportMax));
          setMaxWidth(newMaxWidth);
        }, 100);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isExpanded, maxViewportPercentage, selectors]);

  return {
    maxWidth,
    filterContainerRef,
    recalculateMaxWidth,
  };
};
